import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Lock, LogOut, Trash2, Pencil, Plus, X, Save, Loader2, Mail, FileText, Eye } from 'lucide-react';
import {
  verifyAdmin, getAdminPassword, setAdminPassword, clearAdminPassword,
  listInquiries, deleteInquiry,
  fetchInsights, createInsight, updateInsight, deleteInsight,
} from '../lib/api';

const emptyDraft = { title: '', category: 'Governance', excerpt: '', read_time: '6 min read', date: '', content: '' };

const Login = ({ onSuccess }) => {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr('');
    try {
      await verifyAdmin(pw);
      setAdminPassword(pw);
      onSuccess();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Invalid password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} className="w-full max-w-md rounded-xl border border-white/10 bg-[#0f1a2f]/70 p-8">
        <div className="flex items-center gap-2 text-orange-300 text-[11px] font-mono tracking-[0.22em]">
          <Lock className="w-3.5 h-3.5" /> ADMIN ACCESS
        </div>
        <h1 className="mt-3 font-serif text-white text-3xl">Sign in to admin</h1>
        <p className="mt-2 text-sm text-slate-400">Enter the admin password to manage inquiries and publish insights.</p>
        <label className="mt-6 block text-[11px] font-mono tracking-[0.2em] text-slate-500">PASSWORD</label>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
          autoFocus className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3.5 py-2.5 text-sm text-white outline-none transition-colors" />
        {err && <div className="mt-3 text-sm text-red-400">{err}</div>}
        <button disabled={loading} type="submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-[#0a1120] font-semibold text-sm px-5 py-3 rounded-md transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign in
        </button>
      </motion.form>
    </div>
  );
};

const InquiriesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await listInquiries()); } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm('Delete this inquiry?')) return;
    await deleteInquiry(id);
    if (selected?.id === id) setSelected(null);
    load();
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-2 rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-4">
        <div className="flex items-center justify-between px-2 pb-3 border-b border-white/5">
          <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300">INBOX</div>
          <div className="text-xs text-slate-500">{items.length} total</div>
        </div>
        {loading && <div className="px-2 py-4 text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {!loading && items.length === 0 && <div className="px-2 py-6 text-sm text-slate-400">No inquiries yet.</div>}
        <ul className="mt-2 space-y-1 max-h-[600px] overflow-auto">
          {items.map(i => (
            <li key={i.id}>
              <button onClick={() => setSelected(i)}
                className={`w-full text-left px-3 py-3 rounded-md transition-colors ${selected?.id === i.id ? 'bg-orange-500/10 border border-orange-400/30' : 'hover:bg-white/5 border border-transparent'}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white font-medium truncate">{i.name}</div>
                  <div className="text-[10px] font-mono text-slate-500">{(i.created_at || '').slice(0, 10)}</div>
                </div>
                <div className="text-xs text-slate-400 truncate">{i.org} · {i.scope}</div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-3 rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-6 min-h-[400px]">
        {!selected ? (
          <div className="h-full flex items-center justify-center text-slate-500 text-sm">Select an inquiry to view details</div>
        ) : (
          <div>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300">INQUIRY DETAIL</div>
                <h3 className="mt-1 font-serif text-white text-2xl">{selected.name}</h3>
                <div className="text-sm text-slate-400">{selected.org}</div>
              </div>
              <button onClick={() => onDelete(selected.id)} className="inline-flex items-center gap-1.5 text-sm text-red-300 hover:text-red-200 px-3 py-1.5 rounded-md border border-red-400/30 hover:border-red-400/60 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-6 text-sm">
              <div className="rounded-md border border-white/8 p-3">
                <div className="text-[10px] font-mono tracking-wider text-slate-500">EMAIL</div>
                <a href={`mailto:${selected.email}`} className="text-orange-300 hover:text-orange-200 break-all">{selected.email}</a>
              </div>
              <div className="rounded-md border border-white/8 p-3">
                <div className="text-[10px] font-mono tracking-wider text-slate-500">SCOPE</div>
                <div className="text-slate-200">{selected.scope}</div>
              </div>
              <div className="rounded-md border border-white/8 p-3">
                <div className="text-[10px] font-mono tracking-wider text-slate-500">SUBMITTED</div>
                <div className="text-slate-200">{selected.created_at}</div>
              </div>
              <div className="rounded-md border border-white/8 p-3">
                <div className="text-[10px] font-mono tracking-wider text-slate-500">EMAIL NOTIFICATION</div>
                <div className={selected.email_sent ? 'text-emerald-300' : 'text-slate-400'}>{selected.email_sent ? 'Sent' : 'Not sent (SMTP not configured)'}</div>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-white/8 p-4">
              <div className="text-[10px] font-mono tracking-wider text-slate-500 mb-2">ENGAGEMENT BRIEF</div>
              <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InsightsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or 'new' or null
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await fetchInsights()); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(emptyDraft); setEditing('new'); setPreview(false); };
  const startEdit = (item) => {
    setDraft({
      title: item.title || '', category: item.category || 'Governance',
      excerpt: item.excerpt || '', read_time: item.read_time || '6 min read',
      date: item.date || '', content: item.content || '',
    });
    setEditing(item.id); setPreview(false);
  };
  const closeEditor = () => { setEditing(null); setDraft(emptyDraft); };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...draft };
      if (!payload.date) delete payload.date;
      if (editing === 'new') await createInsight(payload);
      else await updateInsight(editing, payload);
      closeEditor();
      await load();
    } catch (err) { alert(err?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this insight?')) return;
    await deleteInsight(id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300">PUBLISHED INSIGHTS ({items.length})</div>
        <button onClick={startNew}
          className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-[#0a1120] font-semibold text-sm px-4 py-2 rounded-md transition-colors">
          <Plus className="w-4 h-4" /> New Insight
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(it => (
            <div key={it.id} className="rounded-xl border border-white/8 bg-[#0f1a2f]/50 p-5">
              <div className="flex items-center justify-between text-[11px] font-mono tracking-[0.18em] text-slate-500">
                <span className="text-orange-300">{(it.category || '').toUpperCase()}</span>
                <span>{it.date}</span>
              </div>
              <h3 className="mt-3 font-serif text-white text-lg leading-snug">{it.title}</h3>
              <p className="mt-2 text-sm text-slate-400 line-clamp-2">{it.excerpt}</p>
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                <div className="text-xs font-mono text-slate-500">{it.read_time}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(it)} className="inline-flex items-center gap-1 text-sm text-orange-300 hover:text-orange-200 px-2 py-1 rounded-md hover:bg-orange-500/10">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => onDelete(it.id)} className="inline-flex items-center gap-1 text-sm text-red-300 hover:text-red-200 px-2 py-1 rounded-md hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 md:p-10 overflow-auto" onClick={closeEditor}>
          <div className="relative max-w-4xl mx-auto rounded-xl border border-white/10 bg-[#0a1120] p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300">{editing === 'new' ? 'NEW INSIGHT' : 'EDIT INSIGHT'}</div>
                <div className="font-serif text-white text-2xl mt-1">{draft.title || 'Untitled'}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPreview(p => !p)} className="inline-flex items-center gap-1.5 text-sm text-slate-200 hover:text-orange-300 px-3 py-1.5 rounded-md border border-white/15 hover:border-orange-400/60 transition-colors">
                  <Eye className="w-3.5 h-3.5" /> {preview ? 'Edit' : 'Preview'}
                </button>
                <button onClick={closeEditor} className="p-2 rounded-md hover:bg-white/5 text-slate-300"><X className="w-4 h-4" /></button>
              </div>
            </div>

            {!preview ? (
              <form onSubmit={onSave} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">TITLE</label>
                    <input required value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none" />
                  </div>
                  <div>
                    <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">CATEGORY</label>
                    <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                      className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none">
                      {['Governance', 'Safety', 'Program Delivery', 'Analytics', 'Asset Management'].map(c => (
                        <option key={c} value={c} className="bg-[#0a1120]">{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">READ TIME</label>
                    <input value={draft.read_time} onChange={(e) => setDraft({ ...draft, read_time: e.target.value })}
                      className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none" />
                  </div>
                  <div>
                    <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">DATE (leave blank for today)</label>
                    <input placeholder="12 Jun 2026" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                      className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">EXCERPT</label>
                  <textarea required rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none resize-none" />
                </div>
                <div>
                  <label className="text-[11px] font-mono tracking-[0.2em] text-slate-500">CONTENT (Markdown)</label>
                  <textarea required rows={14} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                    className="mt-2 w-full bg-white/[0.03] border border-white/10 focus:border-orange-400/60 rounded-md px-3 py-2 text-sm text-white outline-none font-mono resize-y" />
                  <div className="mt-1 text-[11px] text-slate-500">Supports Markdown: **bold**, ## headings, - lists, [links](url)</div>
                </div>
                <div className="pt-4 flex items-center justify-end gap-3">
                  <button type="button" onClick={closeEditor} className="px-4 py-2 rounded-md border border-white/15 hover:border-white/30 text-sm text-slate-200">Cancel</button>
                  <button type="submit" disabled={saving}
                    className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-60 text-[#0a1120] font-semibold text-sm px-4 py-2 rounded-md transition-colors">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing === 'new' ? 'Publish' : 'Save changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="prose prose-invert prose-sm max-w-none prose-headings:font-serif prose-headings:text-white prose-h2:text-xl prose-p:text-slate-300 prose-strong:text-orange-200">
                <div className="text-[11px] font-mono tracking-[0.18em] text-orange-300">{draft.category?.toUpperCase()} · {draft.read_time}</div>
                <h1 className="font-serif">{draft.title || 'Untitled'}</h1>
                <p className="text-slate-400">{draft.excerpt}</p>
                <hr className="border-white/10" />
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft.content || '_(empty)_'}</ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(!!getAdminPassword());
  const [tab, setTab] = useState('inquiries');

  useEffect(() => {
    // Verify existing token on mount
    if (authed) {
      verifyAdmin(getAdminPassword()).catch(() => { clearAdminPassword(); setAuthed(false); });
    }
  }, [authed]);

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div className="max-w-[1320px] mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono tracking-[0.22em] text-orange-300">ADMIN CONSOLE</div>
          <h1 className="mt-1 font-serif text-white text-3xl">Chainage Advisory — Management</h1>
        </div>
        <button onClick={() => { clearAdminPassword(); setAuthed(false); }}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-orange-400/60 text-slate-200 hover:text-orange-300 font-medium text-sm px-4 py-2 rounded-md transition-colors">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="flex items-center gap-1 bg-white/[0.02] border border-white/5 rounded-lg p-1.5 w-fit mb-6">
        {[
          { key: 'inquiries', label: 'Inquiries', icon: Mail },
          { key: 'insights', label: 'Insights CMS', icon: FileText },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 px-3.5 py-1.5 text-sm rounded-md transition-all ${tab === t.key ? 'bg-orange-500/15 text-orange-300' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'inquiries' && <InquiriesTab />}
      {tab === 'insights' && <InsightsTab />}
    </div>
  );
};

export default Admin;
